import { BrandPageQueryObj } from "@faire/retailer-shared/search/BrandPageQueryObj";
import {
  loggedInBrandLeanProductTilesRequest,
  getEstimatedClientSize,
  searchBrandProductsFiltersRequestFromProductTilesRequest,
  getPageNumber,
  getPageSize,
} from "@faire/retailer-shared/search/loggedInBrandLeanProductTilesRequest";
import getPublishedBrandProductCollectionMetadata from "@faire/web-api/api/brand/brandToken/published-product-collection-metadata/get";
import { getQueryKey as getQueryKeyPublishedBrandProductCollectionMetadata } from "@faire/web-api/api/brand/brandToken/published-product-collection-metadata/get-hooks";
import searchProductsFromBrandFilters from "@faire/web-api/api/v2/search/products/from-brand/filters/post";
import { getQueryKey_escape_hatch as getQueryKeyProductsFromBrandFilters } from "@faire/web-api/api/v2/search/products/from-brand/filters/post-hooks";
import searchProductsFromBrand from "@faire/web-api/api/v2/search/products/from-brand/post";
import { getQueryKey_escape_hatch as getQueryKeyProductsFromBrand } from "@faire/web-api/api/v2/search/products/from-brand/post-hooks";
import { IBrandViewV2Response } from "@faire/web-api/indigofair/data/IBrandViewV2Response";
import { optimizeAvatar } from "@faire/web/slate/Avatar/Avatar";
import { QueryClient } from "@tanstack/react-query";

import { stubImage } from "@faire/retailer/lib/preload-images/common";
import { getSettingEnableCollectionsPagination } from "@faire/retailer/settings/getSettingEnableCollectionsPagination";
import { ProductCacheStore } from "@faire/retailer/stores/domain/ProductCacheStore";
import { buildBannerImageSrcSet } from "@faire/retailer/views/brand/BrandHeader/buildBannerImageSrcSet";
import { createGetBuyItAgainProductsRequest } from "@faire/retailer/views/brand/carousels/ReorderArchetypeCarousels/createGetBuyItAgainProductsRequest";
import { isReorderArchetype } from "@faire/retailer/views/brand/isReorderArchetype";

export function preloadBrandPageLoggedIn(
  brandToken: string,
  queryParams: BrandPageQueryObj,
  queryClient: QueryClient,
  keepPrefetchedBrandViewData: boolean = false
) {
  ProductCacheStore.get()
    .fetchBrandView(brandToken, keepPrefetchedBrandViewData)
    .then((response) => {
      if (isReorderArchetype(response)) {
        ProductCacheStore.get().getBuyItAgainProducts(
          createGetBuyItAgainProductsRequest(brandToken)
        );
      }

      if (response.brand) {
        preloadImagesBrandPageLoggedIn(response.brand);
      }
    });

  const {
    collectionsQueryKey,
    collectionsQueryFn,
    productsQueryKey,
    productsQueryFn,
    productFiltersQueryKey,
    productFiltersQueryFn,
    staleTime,
  } = getBrandProductsUseQueryArgs(brandToken, queryParams);

  queryClient.prefetchQuery({
    queryKey: collectionsQueryKey,
    queryFn: collectionsQueryFn,
    staleTime,
  });

  queryClient.prefetchQuery({
    queryKey: productsQueryKey,
    queryFn: productsQueryFn,
    staleTime,
  });

  queryClient.prefetchQuery({
    queryKey: productFiltersQueryKey,
    queryFn: productFiltersQueryFn,
    staleTime,
  });
}

export function getBrandProductsUseQueryArgs(
  brandToken: string,
  queryParams: BrandPageQueryObj
) {
  const clientSize = getEstimatedClientSize();

  const productsRequest = loggedInBrandLeanProductTilesRequest(
    queryParams,
    clientSize,
    brandToken
  );

  const filtersRequest =
    searchBrandProductsFiltersRequestFromProductTilesRequest(productsRequest);

  const collectionsPagination = getSettingEnableCollectionsPagination()
    ? {
        page: getPageNumber(queryParams),
        pageSize: getPageSize(clientSize),
      }
    : undefined;

  const collectionsQueryKey =
    getQueryKeyPublishedBrandProductCollectionMetadata(
      brandToken,
      collectionsPagination
    );

  const productsQueryKey = getQueryKeyProductsFromBrand(productsRequest);

  const productFiltersQueryKey =
    getQueryKeyProductsFromBrandFilters(filtersRequest);

  return {
    collectionsQueryKey,
    collectionsQueryFn: () =>
      getPublishedBrandProductCollectionMetadata(
        brandToken,
        collectionsPagination
      ),
    productsQueryKey,
    productsQueryFn: () => searchProductsFromBrand(productsRequest),
    productFiltersQueryKey,
    productFiltersQueryFn: () => searchProductsFromBrandFilters(filtersRequest),
    queryKeyString: JSON.stringify([collectionsQueryKey, productsQueryKey]),
    staleTime: 1000 * 60 * 5,
  };
}

// Exported for testing
export const preloadImagesBrandPageLoggedIn = (
  brand: IBrandViewV2Response.ILeanBrand
) => {
  const { profile_image, banner_image } = brand;
  const isSSR = false;

  if (profile_image) {
    const src = optimizeAvatar(profile_image, "x-large", isSSR);
    stubImage(src);
  }

  if (banner_image) {
    const { src, srcset, sizes } = buildBannerImageSrcSet(banner_image);
    stubImage(src, srcset, sizes);
  }
};
